import { Injectable }                        from '@angular/core';
import { ARCHIVING_KEYWORD_METADATA }        from '@app/modules/keyword/config/archiving-keyword.config';
import { ArchivingKeyword }                  from '@app/modules/keyword/models/archiving-keyword.model';
import { BusinessPremisesByKeyword }         from '@app/modules/keyword/models/common-keyword.model';
import { IPropsUpdateArchivingKeywordState } from '@app/modules/keyword/store/archiving-keyword/archiving-keyword.actions';
import { archivingKeywordStore }             from '@app/modules/keyword/store/archiving-keyword/archiving-keyword.store';
import { AbstractBaseService }               from '@core/bases/services/abstractBaseService';
import {
	IArchivingKeywordHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                            from '@core/interfaces/api.interface';
import { IActionItem }                       from '@core/interfaces/entity.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                            from '@core/interfaces/store.interface';
import { ArchivingKeywordApiService }        from '@http/archiving/archiving-keyword-api.service';
import { EntityActionEventEnum }             from '@models/entity.model';
import { KeywordScopesEnum }                 from '@models/scope.enums';
import { Store }                             from '@ngrx/store';
import {
	Observable,
	of
}                                            from 'rxjs';
import {
	take,
	tap
}                                            from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class ArchivingKeywordService extends AbstractBaseService {

	private _archivingKeywordMetadata = ARCHIVING_KEYWORD_METADATA;

	constructor(
		private readonly archivingKeywordApiService: ArchivingKeywordApiService,
		protected readonly store$: Store
	) {
		super( store$, archivingKeywordStore.selectors, archivingKeywordStore.actions );
		this.logger.trace( 'ArchivingKeywordService:constructor' );
	}

	// PRIVATE METHODS

	// OPEN ARCHIVE SPECIFIC SELECTORS ###########################################################################

	// OPEN ARCHIVE SPECIFIC ACTIONS ###########################################################################
	updateStoreState( updateKeywordState: IPropsUpdateArchivingKeywordState ): void {
		this.store$.dispatch( archivingKeywordStore.actions.updateArchivingKeywordState( updateKeywordState ) );
	}

	// CHECKS #################################################################################
	checkAndUpdateEntityState( archivingKeyword: ArchivingKeyword, caller: string ): void {
		caller += ' | ArchivingKeywordService:checkAndUpdateEntityState';
		this.logger.debug( caller );

		// set check affected buttons to loading
		const entityActionsMap = new Map<EntityActionEventEnum, IActionItem>();
		entityActionsMap.set( EntityActionEventEnum.ENTITY_DELETE, { isEnabled: false, showLoader: true } );
		archivingKeyword = this.mapEntityActions( archivingKeyword, entityActionsMap );

		this.storeUpsertOne( archivingKeyword );

		const businessPremisesIdsByKeywordId$: Observable<RequestCompleteListState<BusinessPremisesByKeyword>> =
			this.authorizationService.hasPermission( KeywordScopesEnum.Delete )
			? this.archivingKeywordApiService.businessPremisesIdsByKeywordId( archivingKeyword?.id )
			: of( { requestState: RequestState.NOT_ASKED, list: [], paging: undefined, action: RequestActionEnum.LIST } );

		businessPremisesIdsByKeywordId$
		.pipe( take( 1 ) )
		.subscribe( ( businessPremisesByKeywordState: RequestCompleteListState<BusinessPremisesByKeyword> ) => {
			entityActionsMap.set( EntityActionEventEnum.ENTITY_DELETE, { isEnabled: false, showLoader: false } );


			if ( [ RequestState.NO_RESULT, RequestState.SUCCESS ].includes( businessPremisesByKeywordState.requestState ) ) {
				entityActionsMap.set(
					EntityActionEventEnum.ENTITY_DELETE,
					{
						isEnabled: businessPremisesByKeywordState?.list?.length === 0,
						showLoader: entityActionsMap.get( EntityActionEventEnum.ENTITY_DELETE ).showLoader
					}
				);
			}

			archivingKeyword = this.mapEntityActions( archivingKeyword, entityActionsMap );
			this.storeUpsertOne( archivingKeyword );
		} );
	}


	// NAVIGATION ##########################################################################

	// API #################################################################################
	search(
		params?: IArchivingKeywordHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig,
		getAllPagesData?: boolean
	): Observable<RequestCompleteListState<ArchivingKeyword>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<ArchivingKeyword>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ArchivingKeyword>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingKeywordService:search` };

		params = this.generateParams( params );

		const getAllPagesReq$ = ( [ paramsArg ] ) => this.archivingKeywordApiService.search( paramsArg );
		const req$ = getAllPagesData
								 ? this.requestAllPagesData<ArchivingKeyword>( getAllPagesReq$, [ params ], 0, 100 )
								 : this.archivingKeywordApiService.search( params );
		return this.requestListThroughProxy<ArchivingKeyword>( req$, storeProcessingConfig );
	}

	read(
		id: string,
		withFullResponse?: boolean,
		caller?: string,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingKeyword>(
			storeConfig,
			this.storeEntityById$<ArchivingKeyword>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingKeywordService:read` };

		const req$ = this.archivingKeywordApiService.read( id, withFullResponse );
		return this.requestEntityThroughProxy( id, req$, storeProcessingConfig );
	}


	create(
		archivingKeyword: ArchivingKeyword,
		caller: string,
		withFullResponse: boolean = true,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingKeyword>(
			storeConfig,
			null,
			StoreActionsEnum.ADD_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingKeywordService:create` };

		const req$ = this.archivingKeywordApiService.create( archivingKeyword, withFullResponse );
		return this.requestEntityThroughProxy<ArchivingKeyword>( null, req$, storeProcessingConfig );
	}

	update(
		archivingKeyword: ArchivingKeyword,
		caller: string,
		withFullResponse: boolean = true,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		if ( !archivingKeyword ) {
			return this.handleMissingEntity( RequestActionEnum.UPDATE, RequestState.FAILED, 400, true );
		}

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingKeyword>(
			storeConfig,
			this.storeEntityById$<ArchivingKeyword>( archivingKeyword?.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingKeywordService:update` };

		const req$ = this.archivingKeywordApiService.update( archivingKeyword, withFullResponse );
		return this.requestEntityThroughProxy( archivingKeyword?.id, req$, storeProcessingConfig );
	}

	delete( archivingKeywordId: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingKeyword>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$( archivingKeywordId ),
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this.archivingKeywordApiService.delete( archivingKeywordId )
		.pipe(
			take( 1 ),
			tap( ( state: RequestCompleteEntityState<ArchivingKeyword> ) => {
				if ( state.requestState === RequestState.SUCCESS ) {
					this.storeRemoveOne<ArchivingKeyword>( archivingKeywordId );
				}
			} )
		);

		return this.requestEntityThroughProxy<ArchivingKeyword>( archivingKeywordId, req$, storeProcessingConfig );
	}
}
